import { Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import Dialog from 'modules/common/components/MUI/Dialog';
import AuthenticationPanel from 'modules/Dashboard/common/AuthenticationBanner/AuthenticationPanel';
import useOAuthConnection from 'modules/common/queries/oAuthConnection';
import { SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';

interface ReconnectGustoModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

/**
 * Hosts the Gusto connect button on the reconnect task itself.
 *
 * Unlike the Plaid and Quiltt reconnect components, this one renders a dialog rather than driving a
 * vendor popup and returning null: Gusto's flow is an `OAuth2Login` *button*, so there has to be
 * something for the member to click. `AuthenticationPanel source="gusto"` is that button, and it is
 * the same control payroll setup uses — one Gusto connect implementation, not a second.
 *
 * The task is completed server-side: `POST /oauth/` finishes it inside `create_new_connection`
 * before responding, so this only has to notice the connection went through and refetch. Nothing
 * here transitions the task, which is why there is no `completion_payload`.
 */
export default function ReconnectGustoModal({ open, onClose, task }: Readonly<ReconnectGustoModalProps>) {
    const { oAuthResult } = useOAuthConnection('gusto');
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();
    const connected = !!oAuthResult?.connected;
    // Only a reconnect that happens while the dialog is open should close it. Without this the
    // dialog would refuse to open at all for a member whose connection has since been repaired
    // elsewhere, leaving the stale card with a button that does nothing.
    const wasDisconnectedOnOpen = useRef(false);

    useEffect(() => {
        if (open && !connected) {
            wasDisconnectedOnOpen.current = true;
        }
        if (!open) {
            wasDisconnectedOnOpen.current = false;
        }
    }, [open, connected]);

    useEffect(() => {
        if (open && connected && wasDisconnectedOnOpen.current) {
            invalidateMemberTaskQueries(task);
            onClose();
        }
    }, [open, connected, invalidateMemberTaskQueries, onClose, task]);

    return (
        <Dialog open={open} onClose={onClose} heading={task.task_template.display_name} size="medium">
            <Stack spacing={3}>
                <Typography variant="body1" color="text.secondary">
                    {task.task_template.instructions}
                </Typography>
                <AuthenticationPanel source="gusto" showButtonIcon={false} showCaption={false} />
            </Stack>
        </Dialog>
    );
}
